import React, { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import Icon from 'components/AppIcon';

const EnhancedTable = ({ 
  data, 
  columns, 
  loading = false, 
  onRowClick = null,
  enableSorting = true,
  enableFiltering = true,
  enablePagination = true,
  pageSize = 10,
  searchPlaceholder = "Search...",
  emptyMessage = "No data available",
  mobileCardLayout = false,
  mobileColumns = [],
  responsiveBreakpoint = 'md',
  enableExpandableRows = false,
  expandableRowComponent: ExpandableRowComponent = null,
  expandableRowProps = {},
  mobileActions = [],
  statusFilter = null,
  onStatusFilterChange = null,
  statusFilterOptions = [],
  hideFiltersOnMobile = false,
  useDropdownFiltersOnMobile = false
}) => {
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: pageSize,
  });
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedMobileRows, setExpandedMobileRows] = useState(new Set());

  // Filter data by status if statusFilter is provided
  const filteredData = useMemo(() => {
    if (!statusFilter || statusFilter === 'all') {
      return data;
    }
    return data.filter(item => item.status === statusFilter);
  }, [data, statusFilter]);

  // Mobile detection
  React.useEffect(() => {
    const checkMobile = () => {
      const breakpoints = {
        'sm': 640,
        'md': 768,
        'lg': 1024,
        'xl': 1280
      };
      setIsMobile(window.innerWidth < breakpoints[responsiveBreakpoint]);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [responsiveBreakpoint]);

  const toggleRowExpansion = (rowId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };

  const toggleMobileRowExpansion = (rowId) => {
    const newExpandedRows = new Set(expandedMobileRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedMobileRows(newExpandedRows);
  };

  const getStatusText = (status) => {
    const statusMap = {
      'pending': { text: 'Pending Review', color: 'text-yellow-400' },
      'in-progress': { text: 'In Progress', color: 'text-blue-400' },
      'completed': { text: 'Completed', color: 'text-green-400' },
      'cancelled': { text: 'Cancelled', color: 'text-red-400' },
      'on-hold': { text: 'On Hold', color: 'text-orange-400' }
    };
    return statusMap[status] || { text: status || 'Unknown', color: 'text-gray-400' };
  };

  const getStatusDot = (status) => {
    const statusMap = {
      'pending': 'bg-yellow-400',
      'in-progress': 'bg-blue-400', 
      'completed': 'bg-green-400',
      'cancelled': 'bg-red-400',
      'on-hold': 'bg-orange-400'
    };
    return statusMap[status] || 'bg-gray-400';
  };

  const handleRowClick = (row) => {
    if (enableExpandableRows) {
      toggleRowExpansion(row.id);
    } else if (onRowClick) {
      onRowClick(row);
    }
  };

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel: enableFiltering ? getFilteredRowModel() : undefined,
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    state: {
      sorting,
      globalFilter,
      pagination,
    },
    globalFilterFn: 'includesString',
  });

  const LoadingSkeleton = () => {
    if (isMobile && mobileCardLayout) {
      return (
        <div className="animate-pulse space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="glass-card rounded-lg p-3">
              <div className="flex items-start gap-3">
                {/* Icon skeleton */}
                <div className="w-10 h-10 bg-white/10 rounded-lg flex-shrink-0"></div>
                
                {/* Content skeleton */}
                <div className="flex-1 min-w-0 space-y-2">
                  {/* Title and status */}
                  <div className="flex items-center justify-between">
                    <div className="h-4 bg-white/10 rounded w-3/5"></div>
                    <div className="h-6 bg-white/10 rounded w-16"></div>
                  </div>
                  
                  {/* Client and type */}
                  <div className="h-3 bg-white/10 rounded w-2/5"></div>
                  
                  {/* Details */}
                  <div className="h-3 bg-white/10 rounded w-4/5"></div>
                  
                  {/* Requirements preview */}
                  <div className="h-3 bg-white/10 rounded w-full"></div>
                  
                  {/* Action buttons */}
                  <div className="flex justify-end gap-1">
                    <div className="h-8 w-8 bg-white/10 rounded"></div>
                    <div className="h-8 w-8 bg-white/10 rounded"></div>
                    <div className="h-8 w-8 bg-white/10 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    return (
      <div className="animate-pulse">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="border-b border-white/5">
            <div className="flex items-center p-4 space-x-4">
              {[...Array(columns.length)].map((_, j) => (
                <div key={j} className="flex-1">
                  <div className="h-4 bg-white/10 rounded w-full"></div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Enhanced Search and Controls */}
      {enableFiltering && (
        <div className="space-y-4 mb-6">
          {/* Mobile Search Toggle */}
          {isMobile ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <button 
                  onClick={() => setShowMobileSearch(!showMobileSearch)}
                  className="ios-card-secondary p-3 rounded-xl flex items-center justify-center min-w-[44px] flex-shrink-0"
                  style={{
                    borderRadius: 'var(--ios-radius-xl)',
                    transition: 'all var(--ios-duration-fast) var(--ios-ease-out)'
                  }}
                >
                  <Icon name="Search" size={18} className="ios-text-primary" />
                </button>
                
                {/* Status Filter */}
                {statusFilterOptions.length > 0 && (
                  <div className="flex-1 min-w-0">
                    <select
                      value={statusFilter || 'all'}
                      onChange={(e) => onStatusFilterChange && onStatusFilterChange(e.target.value === 'all' ? null : e.target.value)}
                      className="w-full ios-card-secondary px-3 py-3 rounded-xl ios-text-primary text-sm border-none appearance-none"
                      style={{borderRadius: 'var(--ios-radius-xl)'}}
                    >
                      <option value="all">All Status</option>
                      {statusFilterOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Results count - compact */}
                <div className="ios-card-tertiary px-3 py-2 rounded-lg flex-shrink-0">
                  <span className="ios-text-secondary text-xs font-medium">
                    {table.getRowModel().rows.length}
                  </span>
                </div>
              </div>
              
              {/* Collapsible Search Input */}
              {showMobileSearch && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="relative"
                >
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Icon name="Search" size={18} className="text-white/50" />
                  </div>
                  <input
                    type="text"
                    value={globalFilter ?? ''}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    placeholder={searchPlaceholder}
                    className="w-full pl-12 pr-12 py-3 ios-card-secondary border-none text-white placeholder-white/50 focus:ring-2 focus:ring-primary/50 transition-all duration-200 text-base"
                    style={{borderRadius: 'var(--ios-radius-xl)'}}
                    autoFocus
                  />
                  {globalFilter && (
                    <button
                      onClick={() => setGlobalFilter('')}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center"
                    >
                      <Icon name="X" size={16} className="text-white/50 hover:text-white transition-colors" />
                    </button>
                  )}
                </motion.div>
              )}
              
              {/* Active search indicator - mobile */}
              {globalFilter && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between ios-card-tertiary px-3 py-2 rounded-lg"
                >
                  <div className="flex items-center space-x-2">
                    <Icon name="Search" size={14} className="ios-text-secondary" />
                    <span className="ios-text-secondary text-xs">
                      Searching: <span className="ios-text-primary font-medium">"{globalFilter}"</span>
                    </span>
                  </div>
                  <button
                    onClick={() => setGlobalFilter('')}
                    className="p-1 hover:bg-white/10 rounded transition-colors"
                  >
                    <Icon name="X" size={12} className="ios-text-secondary" />
                  </button>
                </motion.div>
              )}
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="relative flex-1 max-w-sm">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Icon name="Search" size={18} className="text-white/50" />
                </div>
                <input
                  type="text"
                  value={globalFilter ?? ''}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full pl-12 pr-4 py-3 ios-card-secondary border-none text-white placeholder-white/50 focus:ring-2 focus:ring-primary/50 transition-all duration-200 text-base"
                  style={{borderRadius: 'var(--ios-radius-xl)'}}
                />
              </div>
            
            {/* Status Filter Buttons/Dropdown */}
            {statusFilterOptions.length > 0 && (
              <>
                {useDropdownFiltersOnMobile && window.innerWidth < 1024 ? (
                  /* Mobile/Tablet Dropdown Filter */
                  <div className="relative min-w-[160px]">
                    <select
                      value={statusFilter || 'all'}
                      onChange={(e) => onStatusFilterChange && onStatusFilterChange(e.target.value === 'all' ? null : e.target.value)}
                      className="appearance-none w-full bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-xl text-white px-4 py-3 pr-10 focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-200 text-base"
                    >
                      <option value="all" className="bg-gray-800 text-white">All Status</option>
                      {statusFilterOptions.map((option) => (
                        <option key={option.value} value={option.value} className="bg-gray-800 text-white">
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                      <Icon name="ChevronDown" size={18} className="text-white/50" />
                    </div>
                  </div>
                ) : (
                  /* Desktop Filter Buttons */
                  <div className="flex items-center space-x-2 flex-wrap">
                    <button
                      onClick={() => onStatusFilterChange && onStatusFilterChange('all')}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        statusFilter === 'all' || statusFilter === null
                          ? 'bg-primary/30 text-primary border border-primary/30'
                          : 'bg-white/10 hover:bg-white/20 text-white/70 border border-white/20'
                      }`}
                    >
                      All
                    </button>
                    {statusFilterOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => onStatusFilterChange && onStatusFilterChange(option.value)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          statusFilter === option.value
                            ? 'bg-primary/30 text-primary border border-primary/30'
                            : 'bg-white/10 hover:bg-white/20 text-white/70 border border-white/20'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
            
            {/* Filter Pills */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 text-white/70 text-sm bg-white/5 px-3 py-2 rounded-lg backdrop-blur-sm border border-white/10">
                <Icon name="Filter" size={14} />
                <span>
                  {table.getRowModel().rows.length} of {filteredData.length} entries
                </span>
              </div>
              
              {globalFilter && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={() => setGlobalFilter('')}
                  className="flex items-center space-x-1 px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-sm backdrop-blur-sm border border-red-500/30"
                >
                  <Icon name="X" size={14} />
                  <span>Clear</span>
                </motion.button>
              )}
            </div>
          </div>
          )}
          
          {/* Active Search Indicator */}
          {globalFilter && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center space-x-2 text-sm text-white/60 bg-primary/10 px-4 py-2 rounded-lg border border-primary/20"
            >
              <Icon name="Search" size={14} />
              <span>Searching for: <strong className="text-primary">"{globalFilter}"</strong></span>
            </motion.div>
          )}
        </div>
      )}

      {/* Table */}
      <div className={isMobile && mobileCardLayout ? "space-y-2 -mx-3 sm:-mx-4 md:mx-0" : "glass-card rounded-lg overflow-hidden"}>
        {isMobile && mobileCardLayout ? (
          <div className="space-y-2 px-1"> {/* Minimal padding for cards */}
            {loading ? (
              <LoadingSkeleton />
            ) : table.getRowModel().rows.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-white/60">
                  <Icon name="Search" size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="text-lg">{emptyMessage}</p>
                </div>
              </div>
            ) : (
              table.getRowModel().rows.map((row, index) => {
                const isExpanded = expandedMobileRows.has(row.id);
                const statusInfo = getStatusText(row.original.status);
                
                return (
                <motion.div
                  key={row.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.02 }}
                  className="bg-white/5 border-l-0 border-r-0 border-t-0 border-b border-white/10 last:border-b-0 transition-all duration-200 hover:bg-white/10 cursor-pointer"
                  onClick={() => toggleMobileRowExpansion(row.id)}
                >
                  {/* Main card content */}
                  <div className="p-3">
                    <div className="flex items-start gap-3">
                      {/* Icon with status indicator */}
                      <div className="relative flex-shrink-0">
                        <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                          <Icon name="FileText" size={16} className="text-primary" />
                        </div>
                        {/* NEW badge overlay */}
                        {(() => {
                          const createdAt = row.original.createdAt || row.original.created_at;
                          if (!createdAt) return null;
                          const createdDate = createdAt?.toDate ? createdAt.toDate() : new Date(createdAt);
                          const isNew = (new Date() - createdDate) < 24 * 60 * 60 * 1000;
                          return isNew ? (
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border border-slate-900"></div>
                          ) : null;
                        })()}
                      </div>
                      
                      {/* Content area */}
                      <div className="flex-1 min-w-0">
                        {/* Header row with title and status dot */}
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-white font-medium text-sm truncate flex-1 mr-2">
                            {row.original.assignment_title || row.original.class_title || row.original.class_name || 'Untitled'}
                          </h3>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Status dot */}
                            <div className={`w-2 h-2 rounded-full ${getStatusDot(row.original.status)}`}></div>
                            {/* Expand/collapse icon */}
                            <Icon 
                              name={isExpanded ? "ChevronUp" : "ChevronDown"} 
                              size={16} 
                              className="text-white/40" 
                            />
                          </div>
                        </div>
                        
                        {/* Client email */}
                        <div className="flex items-center gap-2 mb-1">
                          <Icon name="Mail" size={12} className="text-white/50" />
                          <span className="text-white/70 text-xs">
                            {row.original.email || row.original.client_name || 'No email'}
                          </span>
                        </div>
                        
                        {/* Deadline */}
                        <div className="flex items-center gap-2">
                          <Icon name="Calendar" size={12} className="text-white/50" />
                          {(() => {
                            const deadline = row.original.deadline;
                            if (!deadline) return <span className="text-white/50 text-xs">No deadline</span>;
                            try {
                              const date = deadline?.toDate ? deadline.toDate() : new Date(deadline);
                              const isOverdue = date < new Date();
                              const isToday = date.toDateString() === new Date().toDateString();
                              const isTomorrow = date.toDateString() === new Date(Date.now() + 86400000).toDateString();
                              
                              let timeText = '';
                              if (isToday) timeText = 'Due Today';
                              else if (isTomorrow) timeText = 'Due Tomorrow';
                              else timeText = `Due ${format(date, 'MMM dd')}`;
                              
                              return (
                                <span className={`text-xs ${isOverdue ? 'text-red-400 font-medium' : 'text-white/70'}`}>
                                  {timeText}
                                </span>
                              );
                            } catch (error) {
                              return <span className="text-white/50 text-xs">Invalid date</span>;
                            }
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded content */}
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t border-white/10 bg-white/10"
                    >
                      <div className="p-3 space-y-3">
                        {/* Status with full details */}
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-white/60 text-xs font-medium block mb-1">Status</span>
                            <span className={`text-xs font-medium ${statusInfo.color}`}>
                              {statusInfo.text}
                            </span>
                          </div>
                          {row.original.assigned_writer && (
                            <div className="text-right">
                              <span className="text-white/60 text-xs font-medium block mb-1">Writer</span>
                              <span className="text-white/90 text-xs">Writer assigned</span>
                            </div>
                          )}
                        </div>

                        {/* Assignment/Class details */}
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          {(row.original.assignment_type || row.original.subject) && (
                            <div>
                              <span className="text-white/60 block font-medium mb-1">
                                {row.original.assignment_type ? 'Type' : 'Subject'}
                              </span>
                              <span className="text-white/90">
                                {row.original.assignment_type || row.original.subject}
                              </span>
                            </div>
                          )}
                          {row.original.pages && (
                            <div>
                              <span className="text-white/60 block font-medium mb-1">Pages</span>
                              <span className="text-white/90">{row.original.pages} pages</span>
                            </div>
                          )}
                          {row.original.duration && (
                            <div>
                              <span className="text-white/60 block font-medium mb-1">Duration</span>
                              <span className="text-white/90">{row.original.duration}</span>
                            </div>
                          )}
                          {row.original.difficulty_level && (
                            <div>
                              <span className="text-white/60 block font-medium mb-1">Level</span>
                              <span className="text-white/90">{row.original.difficulty_level}</span>
                            </div>
                          )}
                        </div>

                        {/* Client details */}
                        {row.original.client_name && (
                          <div>
                            <span className="text-white/60 text-xs font-medium block mb-1">Client</span>
                            <span className="text-white/90 text-xs">{row.original.client_name}</span>
                          </div>
                        )}
                        
                        {/* Requirements/notes */}
                        {(row.original.requirements || row.original.additional_notes) && (
                          <div>
                            <span className="text-white/60 text-xs font-medium block mb-1">
                              {row.original.requirements ? 'Requirements' : 'Notes'}
                            </span>
                            <p className="text-white/80 text-xs leading-relaxed">
                              {row.original.requirements || row.original.additional_notes}
                            </p>
                          </div>
                        )}
                        
                        {/* Action buttons */}
                        {mobileActions.length > 0 && (
                          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/20">
                            {mobileActions.map((action, actionIndex) => (
                              <button
                                key={actionIndex}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  action.onClick && action.onClick(row.original);
                                }}
                                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors shadow-sm ${action.className || 'bg-primary/30 hover:bg-primary/40 text-primary'}`}
                                disabled={action.disabled && action.disabled(row.original)}
                              >
                                <Icon name={action.icon} size={12} />
                                {action.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
                );
              })
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id} className="border-b border-white/10">
                  {enableExpandableRows && (
                    <th className="px-6 py-5 text-left text-sm font-semibold text-white/90 bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-sm border-b border-white/10 w-12">
                      <div className="flex items-center justify-center">
                        <Icon name="ChevronRight" size={16} className="text-white/50" />
                      </div>
                    </th>
                  )}
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className="px-6 py-5 text-left text-sm font-semibold text-white/90 bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-sm border-b border-white/10"
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={`flex items-center space-x-2 ${
                            header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                          }`}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <span>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </span>
                          {header.column.getCanSort() && (
                            <span className="text-white/50">
                              {header.column.getIsSorted() === 'asc' ? (
                                <Icon name="ChevronUp" size={14} />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <Icon name="ChevronDown" size={14} />
                              ) : (
                                <Icon name="ChevronsUpDown" size={14} />
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="p-0">
                    <LoadingSkeleton />
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-12">
                    <div className="text-white/60">
                      <Icon name="Search" size={48} className="mx-auto mb-4 opacity-50" />
                      <p className="text-lg">{emptyMessage}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, index) => (
                  <React.Fragment key={row.id}>
                    <motion.tr
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.02 }}
                      className={`border-b border-white/5 transition-colors ${
                        enableExpandableRows ? 'hover:bg-white/5 cursor-pointer' : 
                        onRowClick ? 'hover:bg-white/5 cursor-pointer' : 'hover:bg-white/5'
                      } ${expandedRows.has(row.id) ? 'bg-white/5' : ''}`}
                      onClick={() => handleRowClick(row)}
                    >
                      {enableExpandableRows && (
                        <td className="px-6 py-5 w-12">
                          <div className="flex items-center justify-center">
                            <motion.div
                              animate={{ rotate: expandedRows.has(row.id) ? 90 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <Icon 
                                name="ChevronRight" 
                                size={16} 
                                className="text-white/60"
                              />
                            </motion.div>
                          </div>
                        </td>
                      )}
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-6 py-5 text-sm text-white/80 border-b border-white/5 last:border-r-0">
                          <div className="min-h-[2.5rem] flex items-center">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        </td>
                      ))}
                    </motion.tr>
                    
                    {/* Expandable Row Content */}
                    {enableExpandableRows && ExpandableRowComponent && (
                      <tr>
                        <td colSpan={columns.length + 1} className="p-0 border-b border-white/5">
                          <ExpandableRowComponent
                            item={row.original}
                            isExpanded={expandedRows.has(row.id)}
                            onToggleExpand={() => toggleRowExpansion(row.id)}
                            {...expandableRowProps}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
          </div>
        )}

        {/* Pagination */}
        {enablePagination && filteredData.length > pageSize && (
          <div className="flex items-center justify-between px-6 py-4 bg-white/5 border-t border-white/10">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="ChevronsLeft" size={16} className="text-white/80" />
              </button>
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="ChevronLeft" size={16} className="text-white/80" />
              </button>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="ChevronRight" size={16} className="text-white/80" />
              </button>
              <button
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="ChevronsRight" size={16} className="text-white/80" />
              </button>
            </div>

            <div className="flex items-center space-x-4 text-sm text-white/70">
              <span>
                Page {table.getState().pagination.pageIndex + 1} of{' '}
                {table.getPageCount()}
              </span>
              <select
                value={table.getState().pagination.pageSize}
                onChange={(e) => table.setPageSize(Number(e.target.value))}
                className="bg-white/10 border border-white/20 rounded px-3 py-1 text-white text-sm"
              >
                {[10, 20, 30, 40, 50].map(size => (
                  <option key={size} value={size} className="bg-gray-800">
                    Show {size}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedTable;